// src/shared/hooks/useDevice.js
// Device detection hook for responsive rendering

import { useState, useEffect, useCallback } from 'react';

// Breakpoint constants
export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
};

export const DEVICE_TYPES = {
  MOBILE: 'mobile',
  TABLET: 'tablet',
  DESKTOP: 'desktop',
};

/**
 * Determines device type based on viewport width
 * @param {number} width - Current viewport width
 * @returns {string} Device type
 */
const getDeviceType = (width) => {
  if (width <= BREAKPOINTS.mobile) {
    return DEVICE_TYPES.MOBILE;
  }
  if (width <= BREAKPOINTS.tablet) {
    return DEVICE_TYPES.TABLET;
  }
  return DEVICE_TYPES.DESKTOP;
};

/**
 * Determines orientation based on viewport dimensions
 * @param {number} width - Current viewport width
 * @param {number} height - Current viewport height
 * @returns {string} 'portrait' or 'landscape'
 */
const getOrientation = (width, height) => {
  return height > width ? 'portrait' : 'landscape';
};

/**
 * Debounce utility for resize handler
 * @param {Function} func - Function to debounce
 * @param {number} wait - Debounce delay in ms
 * @returns {Function} Debounced function
 */
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Custom hook for device detection
 * Provides device type, orientation, and responsive utilities
 *
 * @returns {Object} Device information
 * @property {string} deviceType - 'mobile' | 'tablet' | 'desktop'
 * @property {boolean} isMobile - True if mobile device
 * @property {boolean} isTablet - True if tablet device
 * @property {boolean} isDesktop - True if desktop device
 * @property {string} orientation - 'portrait' | 'landscape'
 * @property {number} width - Current viewport width
 * @property {number} height - Current viewport height
 * @property {boolean} isTouch - True if touch device
 */
export const useDevice = () => {
  // SSR-safe initial state
  const [state, setState] = useState(() => {
    // Default to desktop during SSR
    if (typeof window === 'undefined') {
      return {
        deviceType: DEVICE_TYPES.DESKTOP,
        orientation: 'landscape',
        width: 1920,
        height: 1080,
        isTouch: false,
      };
    }

    const width = window.innerWidth;
    const height = window.innerHeight;

    return {
      deviceType: getDeviceType(width),
      orientation: getOrientation(width, height),
      width,
      height,
      isTouch: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    };
  });

  // Memoized resize handler
  const handleResize = useCallback(() => {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const newDeviceType = getDeviceType(width);
    const newOrientation = getOrientation(width, height);

    setState((prev) => {
      // Only update if values changed to prevent unnecessary re-renders
      if (
        prev.deviceType === newDeviceType &&
        prev.orientation === newOrientation &&
        prev.width === width &&
        prev.height === height
      ) {
        return prev;
      }

      return {
        ...prev,
        deviceType: newDeviceType,
        orientation: newOrientation,
        width,
        height,
      };
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Debounced resize handler (100ms delay for performance)
    const debouncedResize = debounce(handleResize, 100);

    // Initial measurement
    handleResize();

    // Listen for resize events
    window.addEventListener('resize', debouncedResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [handleResize]);

  // Computed boolean helpers
  const isMobile = state.deviceType === DEVICE_TYPES.MOBILE;
  const isTablet = state.deviceType === DEVICE_TYPES.TABLET;
  const isDesktop = state.deviceType === DEVICE_TYPES.DESKTOP;

  return {
    deviceType: state.deviceType,
    isMobile,
    isTablet,
    isDesktop,
    orientation: state.orientation,
    width: state.width,
    height: state.height,
    isTouch: state.isTouch,
  };
};

export default useDevice;
